import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { extra } from "../../../config"
import { log, Storage } from "../../pwa"
import { ThemeContext } from '../ThemeContext'
import { AuthContextState, AuthContextUser } from "./auth-context.types"
import { identityFromGetMePayload } from "./identity-from-getme-payload"
import FullScreenLoading from "../../full-screen-loading"
import { getMe, logOut } from "../../../auth-slice"
import { Ionicons } from "@expo/vector-icons"
import ThemedDialog from "../../../style/dialog"
import api from "../../../api-client"
import type { DeleteAccountPayload } from '@ecountabl/lib'
import { useAppDispatch, useAppSelector } from '../../../store'
const APP_TOKEN_KEY = 'ecountabl-app-token'

export const AuthContext = createContext<AuthContextState | undefined>(undefined)

export const AuthProviderWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const {
    loginWithRedirect,
    logout,
    isLoading,
    user,
    error,
    isAuthenticated,
    getAccessTokenSilently,
  } = useAuth0()
  const { theme } = useContext(ThemeContext)

  const [appTokenSession, setAppTokenSession] = useState<AuthContextUser | null>(null)

  const { status: userStatus } = useAppSelector((state) => state.auth)
  const userLoading = userStatus === 'loading'

  const dispatch = useAppDispatch()

  const getAuth0AccessTokenOnly = useCallback(async (): Promise<string | null> => {
    try {
      if (user?.access_token) return user.access_token
      return await getAccessTokenSilently({
        timeoutInSeconds: 5,
        authorizationParams: {
          theme: theme.name
        }
      })
    } catch (err) {
      log.error(err, { tags: { message: 'error getting Auth0 access token' } })
      return null
    }
  }, [user, getAccessTokenSilently, theme.name])

  // Important: keep function identity stable so consumers' effects don't re-run every render
  const isAuthenticatedImplementation = useCallback(async (): Promise<boolean> => {
    return (!!user && isAuthenticated) || !!appTokenSession
  }, [user, isAuthenticated, appTokenSession])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const auth0Tok = await getAuth0AccessTokenOnly()
    if (auth0Tok) return auth0Tok
    return Storage.getItem(APP_TOKEN_KEY)
  }, [getAuth0AccessTokenOnly])

  const logoutImplementation = useCallback(async (): Promise<void> => {
    let logoutError: unknown
    try {
      setAppTokenSession(null)
      await Storage.deleteItem(APP_TOKEN_KEY)
      api.clearToken()
      // Prevent deep-link bootstrap from auto-triggering login right after logout.
      await logout({
        logoutParams: {
          returnTo: `${extra.webappUrl}/login`,
        }
      })
    } catch (err) {
      logoutError = err
      log.error(err, { tags: { message: 'error logging out via Auth0' } })
    } finally {
      dispatch(logOut())
    }

    if (logoutError) {
      throw logoutError
    }
  }, [dispatch, logout])

  const loginImplementation = useCallback(async (
    prompt: "login" | "none" = "login"
  ): Promise<void> => {
    return loginWithRedirect({
      authorizationParams: {
        prompt: prompt,
        theme: theme.name,
      },
      appState: {
        target: window.location.pathname === '/login' ?
          '/dashboard'
          : `${window.location.pathname}${window.location.search}${window.location.hash}`,
      }
    })
  }, [loginWithRedirect, theme.name])

  const deleteAccountImplementation = useCallback(async (
    payload: DeleteAccountPayload
  ): Promise<void> => {
    const token = await getAccessToken()
    if (!token) {
      throw new Error('Unable to delete account without an access token')
    }
    api.setToken(token)
    await api.deleteUser(payload)
    await logoutImplementation()
  }, [getAccessToken, logoutImplementation])

  const loginWithAppTokenImplementation = useCallback(
    async (token: string) => {
      setAppTokenSession(null)
      await Storage.setItem(APP_TOKEN_KEY, token)
      api.setToken(token)
      const resultAction = await dispatch(getMe())
      if (!getMe.fulfilled.match(resultAction)) {
        await Storage.deleteItem(APP_TOKEN_KEY)
        api.clearToken()
        const msg =
          typeof resultAction.payload === 'string'
            ? resultAction.payload
            : 'Unable to verify session'
        throw new Error(msg)
      }
      try {
        setAppTokenSession(identityFromGetMePayload(resultAction.payload))
      } catch (validationErr) {
        await Storage.deleteItem(APP_TOKEN_KEY)
        api.clearToken()
        const message =
          validationErr instanceof Error
            ? validationErr.message
            : 'Session response was invalid'
        throw new Error(message)
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (isLoading) return

    const syncSession = async () => {
      try {
        if (user && isAuthenticated) {
          try {
            await Storage.deleteItem(APP_TOKEN_KEY)
          } catch {
            log.error('error deleting app token', { tags: { message: 'error deleting app token' } })
          }
          setAppTokenSession(null)
          const token = await getAuth0AccessTokenOnly()
          if (token) {
            api.setToken(token)
            dispatch(getMe())
            return
          }
          await logoutImplementation()
          return
        }

        const stored = await Storage.getItem(APP_TOKEN_KEY)
        if (stored) {
          api.setToken(stored)
          const resultAction = await dispatch(getMe())
          if (getMe.fulfilled.match(resultAction)) {
            try {
              setAppTokenSession(identityFromGetMePayload(resultAction.payload))
              return
            } catch (valErr) {
              log.error(valErr, { tags: { message: 'web app token session: invalid user payload' } })
            }
          }
          await Storage.deleteItem(APP_TOKEN_KEY)
        }

        setAppTokenSession(null)
        api.clearToken()
        dispatch(logOut())
      } catch (e) {
        log.error(e, { tags: { message: 'error syncing web auth context session' } })
      }
    }

    void syncSession()
  }, [
    dispatch,
    getAuth0AccessTokenOnly,
    isAuthenticated,
    isLoading,
    logoutImplementation,
    user,
  ])

  const contextUser: AuthContextUser | null = user
    ? { sub: user.sub as string, email: user.email }
    : appTokenSession

  const combinedLoading = isLoading || userLoading

  return <>
    <AuthContext.Provider
      value={{
        user: contextUser,
        isLoading: combinedLoading,
        error,
        login: loginImplementation,
        loginWithAppToken: loginWithAppTokenImplementation,
        logout: logoutImplementation,
        deleteAccount: deleteAccountImplementation,
        isAuthenticated: isAuthenticatedImplementation,
        getAccessToken
      }}
    >
      {
        combinedLoading ?
        <FullScreenLoading loading /> :
        children
      }
    </AuthContext.Provider>
    {/* universal error dialog */}
    <ThemedDialog
      options={[
        {
          title: "Okay",
          icon: <Ionicons name="checkmark" size={22}/>,
          role: "destructive",
          onPress: () => logoutImplementation()
        }
      ]}
      title="Auth Error"
      visible={!!error}
      errorAnimation={!!error}
      subMessage={error?.message || "Unknown error occurred."}
    />
  </>
}

export const useAuthContext = (): AuthContextState => {
  const context = useContext(AuthContext) as AuthContextState
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthContextProvider')
  }
  return context
}